import { useCallback, useMemo, useReducer } from 'react';

import { useCurrentUser } from '@/react/hooks/useUser';
import {
  get as storageGet,
  set as storageSet,
} from '@/react/hooks/useLocalStorage';

import { TailType } from '@@/LogViewer/types';

export type ControllerState = {
  keyword: string;
  filter: boolean;
  autoRefresh: boolean;
  since: number;
  until: number;
  tail: TailType;
  sinceSeconds: number;
  showTimestamp: boolean;
  wrapLine: boolean;
  showLineNumbers: boolean;
};

type ControllerAction<T extends ControllerState = ControllerState> = {
  [K in keyof Required<T>]: {
    field: K;
    value: T[K];
  };
}[keyof Required<T>];

export type ControllerDispatcher = <
  K extends keyof T,
  T extends ControllerState = ControllerState,
>(
  field: K,
  value: T[K]
) => void;

function createReducerWithStorage<T extends ControllerState = ControllerState>(
  keyPrefix: string,
  savedInStorage: (keyof T)[]
) {
  function storageKey(field: keyof T) {
    return `${keyPrefix}.${String(field)}`;
  }

  function initializer(initial: T): T {
    const state = { ...initial };
    Object.keys(initial).forEach((k) => {
      const key = k as keyof T;
      if (savedInStorage.includes(key)) {
        state[key] = storageGet(storageKey(key), state[key]);
      }
    });

    return state;
  }

  function reducer(state: T, action: ControllerAction): T {
    const { field, value } = action;
    storageSet(storageKey(field), value);
    return {
      ...state,
      [field]: value,
    };
  }

  return { reducer, initializer };
}

const initialState: ControllerState = {
  keyword: '',
  filter: false,
  autoRefresh: true,
  since: 0,
  until: 0,
  tail: 1000,
  sinceSeconds: 0,
  showTimestamp: false,
  showLineNumbers: true,
  wrapLine: true,
};

export function useControllerState(): [ControllerState, ControllerDispatcher] {
  const { user } = useCurrentUser();
  const prefix = `log-viewer.user-${user.Id}`;

  const { reducer, initializer } = useMemo(
    () =>
      createReducerWithStorage(prefix, [
        'autoRefresh',
        'since',
        'until',
        'tail',
        'showLineNumbers',
        'showTimestamp',
        'wrapLine',
      ]),
    [prefix]
  );

  const [state, dispatch] = useReducer(reducer, initialState, initializer);

  const setValue = useCallback(
    <K extends keyof T, T extends ControllerState = ControllerState>(
      field: K,
      value: T[K]
    ) => dispatch({ field, value } as ControllerAction),
    [dispatch]
  );

  return [state, setValue];
}
